import React from 'react';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { Tooltip } from '../../../../../../../ui';

interface UpcomingInterview {
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
  interviewStartTime: number;
}

interface UpcomingInterviewsProps {
  interviews: UpcomingInterview[];
}

export const UpcommingInterviews: React.FC<UpcomingInterviewsProps> = ({
  interviews,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  };

  return (
    <div className='mt-4 rounded-lg border border-slate-100 bg-white p-3'>
      <h2 className='mb-2 text-sm font-semibold text-slate-900'>
        { t(SI.overview.upcomingInterviews) }
      </h2>
      <div className='flex items-center gap-3 overflow-x-auto border-t border-slate-100 py-3'>
        { interviews.length > 0 ? (
          <>
            { interviews.map((interview, index) => {
              const fullName = `${interview.user.firstName} ${interview.user.lastName}`;
              const date = formatDate(interview.interviewStartTime);
              const time = formatTime(interview.interviewStartTime);

              return (
                <div key={index} className='relative flex items-center'>
                  { /* Timeline connector (except for first item) */ }
                  { index !== 0 && (
                    <div className='absolute left-[-24px] top-1/2 w-6 border-dashed border-t border-slate-300' />
                  ) }

                  { /* Interview Card */ }
                  <div className='w-44 rounded-lg border border-slate-200 bg-white p-3 text-center shadow-sm'>
                    <p className='text-xs text-slate-600'>{ date }</p>
                    <div
                        className={`mx-auto mt-2 flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                          index === 0
                            ? 'border-green-500 text-green-500'
                            : 'border-slate-400 text-slate-500'
                        }`}
                    />
                    <h3
                        className={`mt-2 h-8 text-sm font-semibold leading-4 ${
                          index === 0 ? 'text-green-700' : 'text-black'
                        }`}
                    >
                      <Tooltip label={fullName}>{ fullName }</Tooltip>
                    </h3>
                    <p className='mt-1 truncate text-[11px] text-slate-500'>
                      <Tooltip label={interview.user.email}>
                        { interview.user.email }
                      </Tooltip>
                    </p>
                    <p className='text-[11px] text-slate-500'>{ time }</p>
                  </div>
                </div>
              );
            }) }
          </>
        ) : (
          <div className='flex min-h-[72px] w-full items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50/60 text-center'>
            <div>
              <p className='text-xs font-semibold text-slate-600'>
                { t(SI.overview.noUpcoming) }
              </p>
              <p className='mt-0.5 text-[11px] text-slate-400'>
                { t(SI.overview.scheduledWillAppear) }
              </p>
            </div>
          </div>
        ) }
      </div>
    </div>
  );
};
